import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError, adminApi, authApi, type AdminProposalSummary } from '../lib/api';
import { embedContext, useTheme } from '../lib/theme';
import StatusChip from '../components/StatusChip';
import { formatDate, packageSummary, viewsSummary } from '../lib/format';
import {
  filterAdminProposals,
  persistAdminHubFilter,
  restoreAdminHubFilter,
  type AdminHubFilter,
} from '../lib/adminHub';

const FILTERS: { key: AdminHubFilter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'published', label: 'Published' },
  { key: 'signed', label: 'Signed' },
  { key: 'archived', label: 'Archived' },
];

export default function AdminHub() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [proposals, setProposals] = useState<AdminProposalSummary[] | null>(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<AdminHubFilter>(() =>
    restoreAdminHubFilter(() => window.localStorage),
  );
  const [search, setSearch] = useState('');
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    setSigningOut(true);
    try {
      await authApi.logout();
    } catch {
      // The local session is abandoned by navigation even if logout fails.
    } finally {
      setSigningOut(false);
      navigate('/admin/login', { replace: true });
    }
  }

  useEffect(() => {
    let cancelled = false;
    adminApi
      .listProposals()
      .then((result) => {
        if (!cancelled) setProposals(result.proposals);
      })
      .catch((caught) => {
        if (cancelled) return;
        setError(caught instanceof ApiError ? caught.message : 'Could not load proposals.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    persistAdminHubFilter(() => window.localStorage, filter);
  }, [filter]);

  const counts = useMemo(() => {
    const base: Record<AdminHubFilter, number> = {
      all: 0,
      draft: 0,
      published: 0,
      signed: 0,
      archived: 0,
    };
    if (!proposals) return base;
    base.all = proposals.length;
    for (const proposal of proposals) base[proposal.status] += 1;
    return base;
  }, [proposals]);

  const visible = useMemo(() => {
    if (!proposals) return [];
    return filterAdminProposals(proposals, filter, search);
  }, [proposals, filter, search]);

  const trimmedSearch = search.trim();

  return (
    <main className="proposal-hub min-h-screen bg-paper">
      <header className="bg-dark px-7 pb-[52px] pt-11 sm:px-10">
        <div className="mx-auto max-w-[960px]">
          <div className="flex items-center justify-between gap-6">
            <span className="font-display text-lg font-semibold text-paper-ondark">
              Sheahan Studios<span className="text-gold">.</span>
            </span>
            <nav aria-label="Admin" className="flex items-center gap-4 text-sm sm:gap-6">
              {!embedContext.embedded && <button type="button" onClick={toggle} className="text-soft-ondark" aria-label={`Use ${theme === "light" ? "dark" : "light"} theme`}>{theme === "light" ? "Dark" : "Light"}</button>}
              <Link
                to="/admin/security"
                className="text-soft-ondark transition hover:text-paper-ondark"
              >
                Security
              </Link>
              <button
                type="button"
                onClick={() => void signOut()}
                disabled={signingOut}
                className="cursor-pointer border-0 bg-transparent p-0 text-soft-ondark transition hover:text-paper-ondark disabled:cursor-wait disabled:opacity-60"
              >
                Sign out
              </button>
            </nav>
          </div>
          <span className="mono-label mt-9 block text-accent-ondark">// Admin</span>
          <h1 className="mt-4 font-display text-4xl font-medium text-paper-ondark sm:text-[2.6rem]">
            Client <em className="text-accent-ondark italic">proposals</em>
          </h1>
          <p className="mt-2.5 max-w-[44ch] text-[0.95rem] text-soft-ondark">
            Every proposal that's been drafted, sent, or signed. Open one to edit it.
          </p>
        </div>
      </header>

      <div className="mx-auto max-w-[960px] px-7 pb-24 pt-[34px] sm:px-10">
        {error && (
          <div className="mb-6 rounded-xl border border-error bg-error-tint px-5 py-4 text-sm text-error">
            {error}
          </div>
        )}

        {!error && (
          <>
            <div className="mb-[26px] flex flex-wrap items-center justify-between gap-4 max-[760px]:flex-col max-[760px]:items-stretch">
              <div
                role="tablist"
                aria-label="Proposal status"
                className="inline-flex max-w-full overflow-x-auto rounded-full border border-line bg-paper-2 p-[3px]"
              >
                {FILTERS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={filter === key}
                    onClick={() => setFilter(key)}
                    className={
                      'inline-flex shrink-0 items-center gap-2 rounded-full px-[18px] py-2 text-sm font-medium transition max-[420px]:px-3 ' +
                      (filter === key
                        ? 'bg-ink text-paper'
                        : 'text-ink-soft hover:text-ink')
                    }
                  >
                    {label}
                    <span
                      className={
                        'font-mono text-[0.68rem] ' +
                        (filter === key ? 'text-paper-3' : 'text-ink-faint')
                      }
                    >
                      {counts[key]}
                    </span>
                  </button>
                ))}
              </div>

              <label className="inline-flex min-w-[230px] items-center gap-2.5 rounded-full border border-line bg-paper-2 px-[18px] py-2.5 max-[760px]:w-full">
                <SearchIcon />
                <span className="sr-only">Search clients and slugs</span>
                <input
                  type="search"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search clients and slugs"
                  className="w-full border-0 bg-transparent text-sm text-ink outline-none placeholder:text-ink-faint"
                />
              </label>
            </div>

            {proposals === null ? (
              <p className="font-mono text-sm text-ink-faint">Loading proposals…</p>
            ) : visible.length === 0 ? (
              <EmptyState
                eyebrow={proposals.length === 0 ? '// Empty' : '// No matches'}
                heading={
                  proposals.length === 0
                    ? 'No proposals yet.'
                    : trimmedSearch
                      ? `Nothing matches "${trimmedSearch}".`
                      : `No ${filter} proposals.`
                }
                message={
                  proposals.length === 0
                    ? "They'll show up here once one is published from the skill."
                    : trimmedSearch
                      ? 'We searched client names and slugs. Clear the search to see everything again.'
                      : 'Choose another view to see the rest.'
                }
              />
            ) : (
              <div className="flex flex-col gap-3.5">
                {visible.map((proposal) => (
                  <ProposalRow key={proposal.id} proposal={proposal} onOpen={() => navigate(`/admin/p/${proposal.id}`)} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}

function ProposalRow({ proposal, onOpen }: { proposal: AdminProposalSummary; onOpen: () => void }) {
  const canViewLive = proposal.status === 'published' || proposal.status === 'signed';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.target === event.currentTarget && (event.key === 'Enter' || event.key === ' ')) { event.preventDefault(); onOpen(); }
      }}
      className="proposal-row flex cursor-pointer flex-col gap-4 rounded-xl border border-line bg-white px-[26px] py-[22px] shadow-[0_2px_10px_-6px_rgba(11,15,13,0.2)] transition hover:-translate-y-px hover:border-gold-soft hover:shadow-[0_2px_10px_-6px_rgba(11,15,13,0.2)] sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2.5">
          <span className="font-display text-lg text-ink">{proposal.client_name}</span>
          <StatusChip status={proposal.status} />
        </div>
        <div className="mt-1 truncate font-mono text-xs text-ink-faint">/{proposal.slug}</div>
        {proposal.status === 'signed' && proposal.signed_by && (
          <div className="mt-1.5 text-sm text-ink-soft">
            Signed by {proposal.signed_by}
            {proposal.signed_at && ` on ${formatDate(proposal.signed_at)}`}
          </div>
        )}
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-x-6 gap-y-2 sm:justify-end">
        <div className="text-right">
          <div className="font-mono text-sm text-ink">{packageSummary(proposal)}</div>
          <div className="mt-0.5 font-mono text-xs text-ink-faint">{viewsSummary(proposal)}</div>
        </div>
        {canViewLive && (
          <a
            href={`/${proposal.slug}/`}
            target="_blank"
            rel="noreferrer"
            onClick={(event) => event.stopPropagation()}
            className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-accent hover:text-accent-deep"
          >
            View live
            <ArrowIcon />
          </a>
        )}
      </div>
    </div>
  );
}

function EmptyState({
  eyebrow,
  heading,
  message,
}: {
  eyebrow: string;
  heading: string;
  message: string;
}) {
  return (
    <div className="mt-3.5 rounded-xl border border-dashed border-line bg-white px-7 py-14 text-center">
      <span className="mono-label block text-accent">{eyebrow}</span>
      <h2 className="mt-3 font-display text-[1.4rem] font-medium text-ink">{heading}</h2>
      <p className="mt-1 text-[0.92rem] text-ink-soft">{message}</p>
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="shrink-0 text-ink-faint"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  );
}
